import { screenService } from "@/services/screen/screen.service";
import { PageableSearchOptions, ScreenDto } from "@exibra/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import isNil from "lodash.isnil";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const screenApiQueryKeys = {
    screens: "screens",
    screen: (id: number) => `screen-${id}`,
    nextSerialNumber: (tenantId: number) => `next-serial-number-${tenantId}`,
} as const;

/**
 * Hook to fetch screens
 * @param options - The options for the query
 * @returns The screens
 */
export const useScreens = (options: PageableSearchOptions) => {
    return useQuery({
        queryKey: [screenApiQueryKeys.screens],
        queryFn: () => screenService.getScreens(options),
    });
};

/**
 * Hook to fetch the next serial number for the tenant
 * @param tenantId - The tenant id
 * @returns The next serial number
 */
export const useNextSerialNumber = (tenantId: number) => {
    return useQuery({
        queryKey: [screenApiQueryKeys.nextSerialNumber, tenantId],
        queryFn: () => screenService.getNextSerialNumber(tenantId),
        enabled: !!tenantId,
    });
};

/**
 * Hook to pair a screen
 * @returns The pair screen mutation
 */
export const usePairScreen = () => {
    return useMutation({
        mutationFn: ({
            uuid,
            tenantId,
            name,
            serialNumber,
        }: {
            uuid: string;
            tenantId: number;
            name: string;
            serialNumber: string;
        }) => screenService.pairScreen(uuid, tenantId, name, serialNumber),
    });
};

/**
 * Hook to fetch a screen by id
 * @param id - The screen id
 * @returns The screen
 */
export const useScreen = (id?: number) => {
    return useQuery({
        queryKey: [screenApiQueryKeys.screen(id!)],
        queryFn: () => screenService.getScreen(id!),
        enabled: !isNil(id),
    });
};

/**
 * Hook to update a screen
 * @returns The update screen mutation
 */
export const useUpdateScreen = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, screen }: { id: number; screen: Partial<ScreenDto> }) =>
            screenService.updateScreen(id, screen),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: [screenApiQueryKeys.screen(variables.id)] });
            toast.success(t("screens.success.updated"));
        },
        onError: (error) => {
            console.error(error);
            toast.error(t("screens.errors.updateFailed"));
        },
    });
};

/**
 * Hook to assign a template to a screen
 * @returns The assign template mutation
 */
export const useAssignTemplate = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ screenId, templateId }: { screenId: number; templateId?: number }) =>
            screenService.assignTemplate(screenId, templateId),
        onSuccess: (updatedScreen) => {
            if (updatedScreen.id) {
                queryClient.invalidateQueries({
                    queryKey: [screenApiQueryKeys.screen(updatedScreen.id)],
                });
                toast.success(t("screens.success.templateAssigned"));
            }
        },
        onError: (error) => {
            console.error(error);
            toast.error(t("screens.errors.templateAssignFailed"));
        },
    });
};

/**
 * Hook to update screen placeholders
 * @returns The update placeholders mutation
 */
export const useUpdateScreenPlaceholders = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            screenId,
            templateId,
            placeholders,
        }: {
            screenId: number;
            templateId: number;
            placeholders: {
                appraiserName: string;
                tunnelNumber: string;
            };
        }) => screenService.updateScreenPlaceholders(screenId, templateId, placeholders),
        onSuccess: (updatedScreen) => {
            if (updatedScreen.id) {
                queryClient.invalidateQueries({
                    queryKey: [screenApiQueryKeys.screen(updatedScreen.id)],
                });
                toast.success(t("screens.success.placeholdersUpdated"));
            }
        },
        onError: (error) => {
            console.error(error);
            toast.error(t("screens.errors.placeholdersUpdateFailed"));
        },
    });
};

/**
 * Hook to remove template from a screen
 * @returns The remove template mutation
 */
export const useRemoveTemplate = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (screenUuid: string) => screenService.removeTemplate(screenUuid),
        onSuccess: (updatedScreen) => {
            if (updatedScreen.id) {
                queryClient.invalidateQueries({
                    queryKey: [screenApiQueryKeys.screen(updatedScreen.id)],
                });
                toast.success(t("screens.success.templateRemoved"));
            }
        },
        onError: (error) => {
            console.error(error);
            toast.error(t("screens.errors.templateRemoveFailed"));
        },
    });
};
